import React, { useContext, useEffect, useState } from "react";

import {
  AuthenticationResult,
  EventType,
  InteractionStatus
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { PageLoadStateProvider } from "components/common";
import { TranslationProvider } from "components/useTranslations";
import { ConfigContext } from "config";

import LoginPage from "../components/pages/LoginPage";

export type Identity = {
  getAccessToken: () => Promise<string>;
  logout: () => Promise<void>;
};

export const IdentityContext = React.createContext<Identity>({
  getAccessToken: () => Promise.resolve(""),
  logout: async () => {
    await Promise.resolve(null);
  }
});

export type IdentityState = {
  helpUrl: string;
  eulaUrl: string;
  privacyPolicyUrl: string;
  translations?: { [key: string]: string } | undefined;
};

const initState: IdentityState = {
  helpUrl: "https://www.formatsolutions.com/en/contact-us",
  eulaUrl: "https://www.formatsolutions.com/en/cargill-legal-terms-conditions",
  privacyPolicyUrl: "https://www.cargill.com/page/privacy-center",
  translations: undefined
};

const IdentityProvider: React.FC<{ children: React.ReactNode }> = props => {
  const config = useContext(ConfigContext);
  const msal = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [state, setState] = useState<IdentityState>(initState);
  const [homeAccountId, setHomeAccountId] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    // normally all http calls go through useApi but it needs a token to succeed
    // this call is expected to always get a 401 and is just to load the translations and help link

    fetch(config?.reactAppApiEndpoint ?? "").then(
      res => {
        res.json().then(
          (data: IdentityState) => {
            setState(data);
          },
          () => {}
        );
      },
      () => {}
    );
  }, [config]);

  msal.instance.addEventCallback(message => {
    switch (message.eventType) {
      case EventType.LOGIN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
        setHomeAccountId(
          (message?.payload as AuthenticationResult)?.account?.homeAccountId
        );
        break;
    }
  });
  if (isAuthenticated) {
    return (
      <IdentityContext.Provider
        value={{
          getAccessToken: async () => {
            let activeAccount = msal.instance.getActiveAccount();
            if (!activeAccount) {
              activeAccount = msal.instance.getAccountByHomeId(
                homeAccountId ?? ""
              );
              if (!activeAccount) {
                await msal.instance.logoutRedirect({
                  onRedirectNavigate: () => {
                    // Return false if you would like to stop navigation after local logout
                    return false;
                  }
                });

                return "";
              }

              msal.instance.setActiveAccount(activeAccount);
            }

            const tokenResult = await msal.instance.acquireTokenSilent({
              scopes: [config?.reactAppAuthProviderScope ?? ""],
              redirectUri: config?.reactAppAuthProviderRedirectUrl
            });
            return tokenResult.accessToken;
          },
          logout: async () => {
            await msal.instance.logout();
          }
        }}
      >
        {props.children}
      </IdentityContext.Provider>
    );
  }

  const login = async () => {
    await msal.instance.loginRedirect({
      scopes: [config?.reactAppAuthProviderScope ?? ""]
    });
  };

  const isAuthenticating =
    msal.inProgress === InteractionStatus.Startup ||
    msal.inProgress === InteractionStatus.HandleRedirect;

  return (
    <PageLoadStateProvider name="login" isLoaded={!!state.translations}>
      <TranslationProvider translations={state.translations}>
        {() => (
          <LoginPage
            isAuthenticating={isAuthenticating}
            login={login}
            error={null}
          />
        )}
      </TranslationProvider>
    </PageLoadStateProvider>
  );
};

export default IdentityProvider;
